import React from 'react';
import styled, { css } from 'styled-components';
import Badge from './ui/Badge';
import Button from './ui/Button';
import DeviceFrame from './ui/DeviceFrame';
import { ArrowRight, ArrowUpRight } from './ui/Icons';
import type { Project } from '../data/projects';

type Layout = 'feature' | 'standard';

const Card = styled.article<{ $layout: Layout }>`
  display: grid;
  gap: 1.75rem;
  height: 100%;
  padding: clamp(1.25rem, 2.5vw, 2rem);
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--border);
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: var(--border-strong);
    background: var(--surface-hover);
  }

  ${({ $layout }) =>
    $layout === 'feature'
      ? css`
          grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
          align-items: center;

          @media (max-width: 900px) {
            grid-template-columns: 1fr;
          }
        `
      : css`
          grid-template-rows: auto 1fr;
        `}
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
`;

const Meta = styled.p`
  font-size: var(--fs-small);
  color: var(--text-3);
`;

const Name = styled.h3<{ $layout: Layout }>`
  font-size: ${({ $layout }) => ($layout === 'feature' ? 'clamp(1.5rem, 2.4vw, 2rem)' : '1.4rem')};
  font-weight: 600;
  letter-spacing: -0.015em;
`;

const Blurb = styled.p`
  color: var(--text-2);
  font-size: 0.975rem;
`;

const Tags = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;

  li {
    padding: 0.25rem 0.6rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--text-2);
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid var(--border);
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  margin-top: auto;
  padding-top: 0.5rem;
`;

interface ProjectCardProps {
  project: Project;
  layout?: Layout;
  headingLevel?: 'h2' | 'h3';
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, layout = 'standard', headingLevel = 'h3' }) => {
  const image = project.images.card;
  return (
    <Card $layout={layout} aria-labelledby={`card-${project.slug}`}>
      <DeviceFrame
        src={image.src}
        alt={image.alt}
        width={image.width}
        height={image.height}
        sizes={layout === 'feature' ? '(min-width: 900px) 640px, 100vw' : '(min-width: 900px) 560px, 100vw'}
      />
      <Body>
        <HeaderRow>
          <Meta>
            {project.kind} · {project.location}
          </Meta>
          <Badge variant={project.status} />
        </HeaderRow>
        <Name as={headingLevel} $layout={layout} id={`card-${project.slug}`}>
          {project.name}
        </Name>
        <Blurb>{project.blurb}</Blurb>
        <Tags aria-label="Project tags">
          {project.tags.map((tag) => (
            <li key={tag}>{tag}</li>
          ))}
        </Tags>
        <Actions>
          <Button to={`/projects#${project.slug}`} variant="secondary">
            View case study <ArrowRight size={16} />
          </Button>
          {project.status === 'live' && project.url ? (
            <Button href={project.url} variant="ghost">
              Visit site <ArrowUpRight />
              <span className="visually-hidden">(opens in a new tab)</span>
            </Button>
          ) : (
            <Badge variant="coming-soon" />
          )}
        </Actions>
      </Body>
    </Card>
  );
};

export default ProjectCard;
